import type { FacebookQuery, Setup } from './types'
import { parseStringOrBoolean } from '../boolean';
import { onConsentStudioScan } from '../consent-studio';

export * from './types';

function isFacebookQuery(obj: any): obj is FacebookQuery {
    return obj && typeof obj === 'function' && 'push' in obj;
}

/* -------------------------------------------------------
 * Load Meta Pixel script and create fbq
 * ----------------------------------------------------- */
export function addScript(
    f: Window,
    b: Document,
    e: string,
    v: string,
    n?: any,
    t?: HTMLScriptElement,
    s?: HTMLScriptElement
): FacebookQuery {
    if (isFacebookQuery(f.fbq)) return f.fbq as FacebookQuery;

    const runtimeConfig = useRuntimeConfig();
    const isDebug = parseStringOrBoolean(runtimeConfig.public.metapixel?.debug);
    const isEnabled = parseStringOrBoolean(runtimeConfig.public.metapixel?.enabled);

    function log(...args: any[]) {
        if (isDebug) {
            console.log('[Meta-Pixel]', ...args)
        }
    }

    // Core fbq function (Meta queue pattern)
    n = f.fbq = (isEnabled
        ? function () {
            if (isDebug) {
                log(Array.from(arguments).join(', '));
            }

            const cmd = arguments[0] === 'consent';

            if (cmd) {
                const consent = arguments[1];

                if (consent === 'default' || consent === undefined) {
                    log('Consent value is \'default\', \'revoke\' will be used.');
                    arguments[1] = 'revoke';
                } else {
                    log(`Consent value is set, \'${consent}\' will be used.`);
                    n.consent = consent;
                }
            }

            if (n.callMethod) {
                // eslint-disable-next-line prefer-spread, prefer-rest-params
                n.callMethod.apply(n, arguments)
            } else {
                // eslint-disable-next-line prefer-rest-params
                n.queue.push(arguments)
            }
        }
        : function () {
            if (isDebug) {
                log('Event suppressed:', Array.from(arguments).join(', '));
            }
        }) as unknown as FacebookQuery;

    f._fbq = n;

    if (isEnabled) {
        n.initialized = false;
        n.consent = undefined;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        n.disablePushState = true

        t = b.createElement(e) as HTMLScriptElement;
        t.defer = true
        t.src = v

        s = b.getElementsByTagName(e)[0] as HTMLScriptElement;
        s.parentNode!.insertBefore(t, s)
    }

    return n;
}

export function addScriptDefault(): FacebookQuery {
    return addScript(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js'
    )
}

/* -------------------------------------------------------
 * Thin wrapper API (NO STATE)
 * ----------------------------------------------------- */
export function setup(
    $fbq: FacebookQuery = addScriptDefault()
): Setup {
    function init(pixelId: number, autoconfig = false): void {
        // If it is the Consent Studio scanner.
        onConsentStudioScan(() => {
            $fbq.consent = 'grant';
        });

        $fbq('consent', $fbq.consent ?? 'default');
        $fbq('set', 'autoConfig', autoconfig, pixelId);
        $fbq('init', pixelId.toString());

        $fbq.initialized = true;
    }

    function pageView(pixelId?: number) {
        if (pixelId) {
            $fbq('trackSingle', pixelId.toString(), 'PageView');
        } else {
            $fbq('track', 'PageView');
        }
    }

    return {
        $fbq,
        init,
        pageView
    }
}
